import { format, parse, lastDayOfMonth } from 'date-fns'
import { sv } from 'date-fns/locale'

/**
 * Utility functions for handling monthly periods in "yyyy-mm-01" format
 */

/**
 * Format period string for display in Swedish
 * @param period - Period string in format "yyyy-mm-01" (e.g., "2025-08-01")
 * @returns Formatted display string (e.g., "Augusti 2025")
 */
export function formatPeriodDisplay(period: string): string {
  try {
    const date = parse(period, 'yyyy-MM-dd', new Date())
    return format(date, 'MMMM yyyy', { locale: sv })
  } catch (error) {
    console.error('Invalid period format:', period)
    return period
  }
}

/**
 * Generate period string from a date
 * @param date - Any date within the desired month
 * @returns Period string in format "yyyy-mm-01"
 */
export function generatePeriodFromDate(date: Date): string {
  return format(date, 'yyyy-MM-01')
}

/**
 * Generate period string from year and month
 * @param year - Full year (e.g., 2025)
 * @param month - Month (1-12)
 * @returns Period string in format "yyyy-mm-01"
 */
export function generatePeriod(year: number, month: number): string {
  const date = new Date(year, month - 1, 1) // month is 0-indexed in Date constructor
  return format(date, 'yyyy-MM-01')
}

/**
 * Validate period format
 * @param period - Period string to validate
 * @returns True if valid format "yyyy-mm-01"
 */
export function validatePeriodFormat(period: string): boolean {
  const regex = /^\d{4}-\d{2}-01$/
  if (!regex.test(period)) {
    return false
  }

  try {
    const date = parse(period, 'yyyy-MM-dd', new Date())
    // Check if the parsed date is valid and matches the first day of month
    return format(date, 'yyyy-MM-dd') === period && date.getDate() === 1
  } catch {
    return false
  }
}

/**
 * Extract month and year from period string
 * @param period - Period string in format "yyyy-mm-01"
 * @returns Object with year and month numbers
 */
export function getPeriodMonthYear(period: string): { year: number; month: number } {
  try {
    const date = parse(period, 'yyyy-MM-dd', new Date())
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1 // getMonth() is 0-indexed
    }
  } catch (error) {
    console.error('Invalid period format:', period)
    throw new Error(`Invalid period format: ${period}`)
  }
}

/**
 * Get the current month as a period string
 * @returns Current month period in format "yyyy-mm-01"
 */
export function getCurrentPeriod(): string {
  return generatePeriodFromDate(new Date())
}

/**
 * Get a list of period options for a select dropdown
 * @param monthsBack - Number of months back to include (default: 12)
 * @param monthsForward - Number of months forward to include (default: 1)
 * @returns Array of period options with value and label
 */
export function getPeriodOptions(monthsBack: number = 12, monthsForward: number = 1): Array<{ value: string; label: string }> {
  const options: Array<{ value: string; label: string }> = []
  const now = new Date()

  // Add future months
  for (let i = monthsForward; i >= 1; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1)
    const period = generatePeriodFromDate(date)
    options.push({
      value: period,
      label: formatPeriodDisplay(period)
    })
  }

  // Add current month
  const currentPeriod = getCurrentPeriod()
  options.push({
    value: currentPeriod,
    label: formatPeriodDisplay(currentPeriod)
  })

  // Add past months
  for (let i = 1; i <= monthsBack; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const period = generatePeriodFromDate(date)
    options.push({
      value: period,
      label: formatPeriodDisplay(period)
    })
  }

  return options
}

/**
 * Sort periods in descending order (newest first)
 * @param periods - Array of period strings
 * @returns Sorted array of periods
 */
export function sortPeriodsDesc(periods: string[]): string[] {
  return periods.sort((a, b) => b.localeCompare(a))
}

/**
 * Sort periods in ascending order (oldest first)
 * @param periods - Array of period strings
 * @returns Sorted array of periods
 */
export function sortPeriodsAsc(periods: string[]): string[] {
  return periods.sort((a, b) => a.localeCompare(b))
}

/**
 * Check if a period is the current month
 * @param period - Period string to check
 * @returns True if the period represents the current month
 */
export function isCurrentPeriod(period: string): boolean {
  return period === getCurrentPeriod()
}

/**
 * Get unique periods from an array of earnings or compensations
 * @param items - Array of items with period field
 * @returns Sorted array of unique periods (newest first)
 */
export function getUniquePeriods(items: Array<{ period: string }>): string[] {
  const uniquePeriods = [...new Set(items.map(item => item.period))]
  return sortPeriodsDesc(uniquePeriods)
}

// ============================================================================
// New functions for Period-centric architecture (Phase 1.1)
// ============================================================================

/**
 * Format period string for short display in Swedish
 * @param period - Period string in format "yyyy-mm-01" (e.g., "2025-08-01")
 * @returns Short formatted display string (e.g., "aug. 2025")
 */
export function formatPeriodShort(period: string): string {
  try {
    const date = parse(period, 'yyyy-MM-dd', new Date())
    return format(date, 'MMM yyyy', { locale: sv })
  } catch (error) {
    console.error('Invalid period format:', period)
    return period
  }
}

/**
 * Generate a display name from period dates
 * For standard monthly periods: "november 2025"
 * For custom periods spanning multiple months: "nov. 2025 - dec. 2025"
 * @param startDate - First day of period
 * @param endDate - Last day of period
 * @returns Generated display name
 */
export function generatePeriodName(startDate: Date, endDate: Date): string {
  const startMonth = startDate.getMonth()
  const startYear = startDate.getFullYear()
  const endMonth = endDate.getMonth()
  const endYear = endDate.getFullYear()

  // Same month and year - standard monthly period
  if (startMonth === endMonth && startYear === endYear) {
    return format(startDate, 'MMMM yyyy', { locale: sv })
  }

  // Different months - show range
  const startStr = format(startDate, 'MMM yyyy', { locale: sv })
  const endStr = format(endDate, 'MMM yyyy', { locale: sv })
  return `${startStr} - ${endStr}`
}

/**
 * Get start and end dates for a monthly period
 * @param period - Period string in format "yyyy-mm-01"
 * @returns Object with startDate (first day) and endDate (last day of month)
 */
export function getMonthlyPeriodDates(period: string): { startDate: Date; endDate: Date } {
  const startDate = parse(period, 'yyyy-MM-dd', new Date())
  const endDate = lastDayOfMonth(startDate)

  return { startDate, endDate }
}